/* eslint-disable camelcase */
import { faBoxesStacked, faFax, faHammer, faPrint } from '@fortawesome/free-solid-svg-icons';
import { faCheck, faClock, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import PostProcessorTypesIcon from 'src/components/icons/PostProcessorTypesIcon';
import PrinterTypeIcon from 'src/components/icons/PrinterTypeIcon';
import { API_RESOURCES, WORK_INSTRUCTION_TYPES } from 'src/utils/constants';
import routes from 'src/utils/routes';

/* ── Event status (wire values from nautilus MaintenanceEventStatus) ── */
export const MAINTENANCE_EVENT_STATUS = {
  PLANNED: 'created', // NOTE: the UI label is "Planned"; the wire value is `created`
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
  CANCELLED: 'cancelled',
};

export const MAINTENANCE_EVENT_STATUS_DISPLAY = {
  [MAINTENANCE_EVENT_STATUS.PLANNED]: { label: 'Planned', variant: 'info' },
  [MAINTENANCE_EVENT_STATUS.IN_PROGRESS]: { label: 'In progress', variant: 'warning' },
  [MAINTENANCE_EVENT_STATUS.COMPLETE]: { label: 'Complete', variant: 'success' },
  [MAINTENANCE_EVENT_STATUS.CANCELLED]: { label: 'Cancelled', variant: 'default' },
};

/* ── Fulfillment status (wire values from MaintenanceRequirementFulfillmentStatus) ── */
export const FULFILLMENT_STATUS = {
  SELECTED: 'selected', // UI label: "Not started"
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
  SKIPPED: 'skipped',
};

export const FULFILLMENT_TERMINAL_STATUSES = [
  FULFILLMENT_STATUS.COMPLETE,
  FULFILLMENT_STATUS.SKIPPED,
];

/* ── Downtime status (subset we care about client-side) ── */
export const DOWNTIME_STATUS = {
  SCHEDULED: 'scheduled',
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
};

/* ── Anchor scopes ── */
export const MAINTENANCE_SCOPE = {
  INSTANCE: 'instance',
  TYPE: 'type',
};

/* ── Maintenance due status config ── */
export const MAINTENANCE_DUE_STATUS = {
  overdue: {
    statusIcon: faClock,
    statusLabel: 'Overdue',
    variant: 'error',
  },
  warning: {
    statusIcon: faTriangleExclamation,
    statusLabel: 'Warning',
    variant: 'warning',
  },
  healthy: {
    statusIcon: faCheck,
    statusLabel: 'OK',
    variant: 'success',
  },
};

/**
 * Anchor registry, keyed by target_table_name. Each instance anchor knows its
 * type pair (for the additive "from type" requirements), whether it can link a
 * downtime, and its display treatment (scope-coded icon + label).
 */
export const MAINTENANCE_ANCHORS = {
  printer: {
    tableName: 'printer',
    apiResource: API_RESOURCES.PRINTER,
    resourceRoute: routes.printerMaterial,
    scope: MAINTENANCE_SCOPE.INSTANCE,
    typeTableName: 'printer_type',
    typeApiResource: API_RESOURCES.PRINTER_TYPE,
    typeUriField: 'printer_type',
    eventTarget: true,
    canLinkDowntime: true,
    downtimeWorkstationField: 'printer',
    instanceLabel: 'On This Printer',
    typeLabel: 'From Printer Type',
    instanceIcon: faPrint,
    typeIcon: PrinterTypeIcon,
  },
  post_processor: {
    tableName: 'post_processor',
    apiResource: API_RESOURCES.POST_PROCESSOR,
    resourceRoute: routes.postProcessorQueue,
    scope: MAINTENANCE_SCOPE.INSTANCE,
    typeTableName: 'post_processor_type',
    typeApiResource: API_RESOURCES.POST_PROCESSOR_TYPE,
    typeUriField: 'post_processor_type',
    eventTarget: true,
    canLinkDowntime: true,
    downtimeWorkstationField: 'post_processor',
    instanceLabel: 'On This Post-Processor',
    typeLabel: 'From Post-Processor Type',
    instanceIcon: faFax,
    typeIcon: PostProcessorTypesIcon,
  },
  tooling_stock: {
    tableName: 'tooling_stock',
    apiResource: API_RESOURCES.TOOLING_STOCK,
    resourceRoute: routes.tool,
    scope: MAINTENANCE_SCOPE.INSTANCE,
    typeTableName: 'tooling_type',
    typeApiResource: API_RESOURCES.TOOLING_TYPE,
    typeUriField: 'type',
    eventTarget: true,
    canLinkDowntime: false, // tooling has no downtime model
    downtimeWorkstationField: null,
    instanceLabel: 'On This Tool',
    typeLabel: 'From Tool Type',
    instanceIcon: faBoxesStacked,
    typeIcon: faHammer,
  },
};

/* ── Frequency ── */
export const MAINTENANCE_FREQUENCY_TYPES = {
  USAGE_COUNT: 'usage_count',
  CALENDAR: 'calendar',
};

export const MAINTENANCE_CALENDAR_MODES = {
  RELATIVE: 'relative',
  FIXED: 'fixed',
};

/* ── Report-type labels shown in requirement step previews (mockup wording) ── */
export const REPORT_TYPE_LABELS = {
  [WORK_INSTRUCTION_TYPES.NO_ENTRY]: 'Acknowledge',
  [WORK_INSTRUCTION_TYPES.CHECKBOX]: 'Yes / No',
  [WORK_INSTRUCTION_TYPES.NUMBER]: 'Number',
  [WORK_INSTRUCTION_TYPES.RANGE]: 'Range',
  [WORK_INSTRUCTION_TYPES.TEXT]: 'Text',
  [WORK_INSTRUCTION_TYPES.PICTURE]: 'Photo',
  [WORK_INSTRUCTION_TYPES.DOCUMENT]: 'Document',
  [WORK_INSTRUCTION_TYPES.SINGLE_SELECT_DROPDOWN]: 'Choice',
  [WORK_INSTRUCTION_TYPES.OUTPUT_AT_LOCATION]: 'Amount / location',
};

/* ── Flow steps (drive the pinned stepper + internal state machine) ── */
export const MAINTENANCE_VIEW = {
  HOME: 'home',
  SELECT: 'select',
  START: 'start',
  RUN: 'run',
  COMPLETE: 'complete',
};

/** The 4-step operator wizard shown under the app-bar (Home is not a step). */
export const MAINTENANCE_STEPS = [
  { key: MAINTENANCE_VIEW.SELECT, label: 'Choose' },
  { key: MAINTENANCE_VIEW.START, label: 'Plan' },
  { key: MAINTENANCE_VIEW.RUN, label: 'Do the work' },
  { key: MAINTENANCE_VIEW.COMPLETE, label: 'Finish' },
];

/* ── Downtime linking choices on the Start screen ── */
export const DOWNTIME_CHOICE = {
  NONE: 'none',
  CREATE: 'create',
  EXISTING: 'existing',
};
